import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useSelector } from "react-redux";
import {
  selectBrokers,
  selectFunds,
  selectStrategies,
} from "../../../Redux/Reducers/selectors";
import {
  findBrokerAbb,
  findFund,
  findStrategy,
  hashAction,
  hashPosition,
} from "../../../component/Methods";
import { resolveEditRoutingDisplay } from "../../TradeBlotter/orderDialogHelpers";
import {
  buildTradeTypeDisplay,
  formatOrderTimestamp,
} from "../../TradeBlotter/tradeBlotterHelpers";

function sumDistributionShares(order) {
  const d = order?.distribution;
  if (!d || typeof d !== "object") return null;
  const vals = Object.values(d);
  if (!vals.length) return null;
  const n = vals.reduce((acc, v) => acc + (Number(v) || 0), 0);
  return Number.isFinite(n) ? n : null;
}

/** Same labels and open/close coloring as Trade Blotter (`buildTradeTypeDisplay`). */
function resolvePreviewTradeType(order) {
  const action = hashAction(order?.action);
  const position = hashPosition(order?.position);
  if (action == null && position == null) {
    return { tradeType: "", isOpeningPosition: false };
  }
  const isOptionTrade = Number(order?.ticker?.investment) === 10;
  return buildTradeTypeDisplay(action, position, isOptionTrade);
}

function formatPreviewOrderType(type) {
  if (type === 1 || type === "1") return "Market";
  if (type === 0 || type === "0") return "Limit";
  if (type == null || type === "") return "—";
  return String(type);
}

/** Same `sx` as `TradeBlotterTable` trade-type column (default Chip radius, no custom corners). */
function tradeTypeChipSx(theme, isOpening) {
  return {
    fontWeight: 600,
    fontSize: "0.75rem",
    maxHeight: "20px",
    color: isOpening
      ? theme.palette.mode === "dark"
        ? "#81c784"
        : "#2e7d32"
      : theme.palette.mode === "dark"
        ? "#e57373"
        : "#c62828",
    border: `1px solid ${
      isOpening
        ? theme.palette.mode === "dark"
          ? "#66bb6a"
          : "#c8e6c9"
        : theme.palette.mode === "dark"
          ? "#ef5350"
          : "#ffcdd2"
    }`,
  };
}

/** Dense header/body cells — aim near DataGrid `density="compact"` + `rowHeight={40}`. */
const headCellSx = (theme) => ({
  fontWeight: 600,
  fontSize: "12px",
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.primary,
  borderBottom: `2px solid ${theme.palette.divider}`,
  whiteSpace: "nowrap",
  py: 0.75,
  lineHeight: 1.2,
});

const bodyCellSx = (theme) => ({
  fontSize: "12px",
  borderColor: theme.palette.divider,
  color: theme.palette.text.primary,
  py: "6px",
  lineHeight: 1.2,
});


const CloseStrategyPreviewModal = ({
  open,
  preview,
  onCancel,
  onConfirmSubmit,
}) => {
  const theme = useTheme();
  const brokers = useSelector(selectBrokers);
  const funds = useSelector(selectFunds);
  const strategies = useSelector(selectStrategies);

  const fundLabel = useMemo(() => {
    if (!preview?.fundId) return "—";
    return findFund(preview.fundId, funds) || preview.fundId;
  }, [preview?.fundId, funds]);

  const strategyLabel = useMemo(() => {
    if (!preview?.strategyId) return "—";
    return findStrategy(preview.strategyId, strategies) || preview.strategyId;
  }, [preview?.strategyId, strategies]);

  const summary = preview?.summary || {};
  const orders = preview?.orders || [];
  const hSx = headCellSx(theme);
  const cSx = bodyCellSx(theme);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 600, fontSize: "16px" }}>Close Trades — Preview Orders</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} title={preview?.fundId}>
          <strong>Fund:</strong> {fundLabel}
          <br />
          <strong>Strategy:</strong> {strategyLabel}
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
          Summary
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Chip
            size="small"
            variant="outlined"
            label={`Real lots · ${summary.realLotCount ?? "—"}`}
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              borderColor: theme.palette.success.main,
              color: theme.palette.mode === "dark" ? theme.palette.success.light : theme.palette.success.dark,
              bgcolor: alpha(theme.palette.success.main, 0.12),
            }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Crossing lots · ${summary.crossingLotCount ?? "—"}`}
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              borderColor: theme.palette.info.main,
              color: theme.palette.mode === "dark" ? theme.palette.info.light : theme.palette.info.dark,
              bgcolor: alpha(theme.palette.info.main, 0.12),
            }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Held-away lots · ${summary.heldAwayLotCount ?? "—"}`}
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              borderColor: theme.palette.warning.main,
              color: theme.palette.mode === "dark" ? theme.palette.warning.light : theme.palette.warning.dark,
              bgcolor: alpha(theme.palette.warning.main, 0.14),
            }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`Orders · ${summary.orderCount ?? orders.length}`}
            sx={{
              fontWeight: 600,
              fontSize: "0.75rem",
              borderColor: theme.palette.primary.main,
              color: theme.palette.mode === "dark" ? theme.palette.primary.light : theme.palette.primary.dark,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }}
          />
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Proposed orders ({orders.length})
        </Typography>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            maxHeight: "50vh",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              tableLayout: "fixed",
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...hSx, width: "11%" }}>Timestamp</TableCell>
                <TableCell sx={{ ...hSx, width: "12%" }}>Trade type</TableCell>
                <TableCell sx={{ ...hSx, width: "10%" }}>Ticker</TableCell>
                <TableCell align="right" sx={{ ...hSx, width: "11%" }}>
                  Ordered shares
                </TableCell>
                <TableCell sx={{ ...hSx, width: "12%" }}>Broker</TableCell>
                <TableCell sx={{ ...hSx, width: "9%" }}>Routing</TableCell>
                <TableCell sx={{ ...hSx, width: "14%" }}>Instructions</TableCell>
                <TableCell sx={{ ...hSx, width: "8%" }}>Order type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order, idx) => {
                const shares = sumDistributionShares(order);
                const broker = findBrokerAbb(order?.broker, brokers) || order?.broker || "—";
                const routing = resolveEditRoutingDisplay(order?.routing);
                const { tradeType, isOpeningPosition } = resolvePreviewTradeType(order);
                const typeLabel = tradeType || "—";
                return (
                  <TableRow
                    key={order.tradeSubtype || idx}
                    sx={{
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <TableCell sx={cSx}>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ fontWeight: 400, fontFamily: "monospace", fontSize: "12px" }}
                      >
                        {formatOrderTimestamp(order?.date)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cSx}>
                      <Chip
                        label={typeLabel}
                        size="small"
                        sx={tradeTypeChipSx(theme, isOpeningPosition)}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cSx, fontWeight: 600 }}>
                      {(order?.ticker?.symbol || "—").toString().toUpperCase()}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        ...cSx,
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 600,
                        fontFamily: "monospace",
                        fontSize: "12px",
                      }}
                    >
                      {shares != null ? shares.toLocaleString() : "—"}
                    </TableCell>
                    <TableCell sx={cSx}>{broker}</TableCell>
                    <TableCell sx={cSx}>{routing}</TableCell>
                    <TableCell
                      sx={{
                        ...cSx,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={order?.instructions}
                    >
                      {order?.instructions || "—"}
                    </TableCell>
                    <TableCell sx={cSx}>{formatPreviewOrderType(order?.type)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={onConfirmSubmit}>
          Close Trades
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseStrategyPreviewModal;
